import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, type HubInfo, type ProjectDetail, type RunSummary } from '@/api/client';
import { useAuth } from '@/auth/store';
import { useDocumentTitle } from '@/compliance/ResearchUse';
import { ConfirmDialog } from './ConfirmDialog';
import { formatBytes, formatDate } from './format';
import { JobsPanel } from './JobsPanel';
import { MetricsBlock } from './MetricsBlock';
import { RunPipelineDialog } from './RunPipelineDialog';

export default function ProjectPage() {
  const { projectId = '' } = useParams();
  const navigate = useNavigate();
  const isAdmin = useAuth((s) => s.me?.user.role === 'admin');
  const [project, setProject] = useState<ProjectDetail | null>(null);
  const [hub, setHub] = useState<HubInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [running, setRunning] = useState(false);
  const [deletingProject, setDeletingProject] = useState(false);
  const [deletingRun, setDeletingRun] = useState<RunSummary | null>(null);
  useDocumentTitle(project ? `Valvia - ${project.name}` : 'Valvia');

  const load = useCallback(() => {
    api
      .get<ProjectDetail>(`/api/projects/${projectId}`)
      .then((p) => {
        setProject(p);
        setName(p.name);
        setNotes(p.notes);
      })
      .catch((e) => setError((e as Error).message));
  }, [projectId]);
  useEffect(load, [load]);
  useEffect(() => {
    if (!isAdmin) return;
    api
      .get<HubInfo>('/api/hub')
      .then(setHub)
      .catch(() => setHub(null));
  }, [isAdmin]);

  async function save() {
    try {
      const p = await api.patch<ProjectDetail>(`/api/projects/${projectId}`, { name, notes });
      setProject({ ...p, runs: project?.runs ?? [] });
      setEditing(false);
    } catch (e) {
      setError((e as Error).message);
    }
  }

  if (error) return <div className="page error">{error}</div>;
  if (!project) return <div className="page muted">Loading…</div>;
  const lastRun = project.runs.find((r) => r.id === project.last_run_id) ?? project.runs[0];

  return (
    <div className="page project-page">
      <div className="breadcrumb">
        <Link to="/">Projects</Link> / {project.name}
      </div>
      <div className="project-head">
        <div className="thumb large">
          {lastRun?.thumbnail_url ? <img src={lastRun.thumbnail_url} alt="" /> : <div className="thumb-empty">no preview</div>}
        </div>
        <div className="project-meta">
          {editing ? (
            <>
              <input value={name} onChange={(e) => setName(e.target.value)} className="title-input" />
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={5} placeholder="Notes" />
              <div className="row">
                <button onClick={() => void save()}>Save</button>
                <button className="secondary" onClick={() => setEditing(false)}>
                  Cancel
                </button>
              </div>
            </>
          ) : (
            <>
              <h1>{project.name}</h1>
              <div className="muted small">Created {formatDate(project.created_at)}</div>
              <p className="notes">{project.notes || <span className="muted">No notes</span>}</p>
              <div className="row wrap">
                {lastRun && <button onClick={() => navigate(`/runs/${lastRun.id}`)}>Open latest run</button>}
                <button className="secondary" onClick={() => setEditing(true)}>
                  Rename / notes
                </button>
                {isAdmin && hub?.configured && (
                  <button className="secondary" onClick={() => setRunning(true)} title={hub.online ? 'Upload a scan and run the pipeline' : 'The pipeline hub is not reachable'}>
                    Run pipeline
                  </button>
                )}
                {isAdmin && (
                  <button className="secondary danger" onClick={() => setDeletingProject(true)}>
                    Delete project
                  </button>
                )}
              </div>
            </>
          )}
        </div>
        <div className="project-metrics">
          {lastRun?.measurements_url ? <MetricsBlock url={lastRun.measurements_url} centerlineUrl={lastRun.centerline_url} /> : <div className="metrics-note muted">No measurements</div>}
        </div>
      </div>

      <JobsPanel projectId={projectId} hub={hub} isAdmin={!!isAdmin} onRunsChanged={load} />

      <h2>Runs</h2>
      {project.runs.length === 0 && (
        <div className="empty">
          No runs yet.{' '}
          {isAdmin && hub?.configured
            ? 'Run the pipeline on a scan, then ingest the result from a workstation.'
            : 'Runs are added with the ingest CLI.'}
        </div>
      )}
      {project.runs.length > 0 && (
        <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Run</th>
              <th>Pipeline</th>
              <th>Frames</th>
              <th>Finished</th>
              <th>Ingested</th>
              <th>Assets</th>
              <th>Reports</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {project.runs.map((r) => (
              <tr key={r.id} className={r.id === project.last_run_id ? 'current' : ''}>
                <td className="mono span" data-label="Run">
                  {r.id}
                </td>
                <td data-label="Pipeline">
                  <span className="badge">{r.pipeline_version ?? '?'}</span>
                </td>
                <td data-label="Frames">{r.is_all_frames ? `${r.num_frames} (4D)` : r.num_frames}</td>
                <td data-label="Finished">{formatDate(r.finished_at)}</td>
                <td data-label="Ingested">{formatDate(r.ingested_at)}</td>
                <td data-label="Assets">
                  {r.asset_count} · {formatBytes(r.total_bytes)}
                </td>
                <td data-label="Reports">
                  {(r.reports ?? []).length > 0 ? (
                    <div className="report-links small">
                      {(r.reports ?? []).map((rep) =>
                        rep.url ? (
                          <a key={rep.name} href={rep.url} target="_blank" rel="noreferrer" title={`${rep.filename} · ${formatBytes(rep.bytes)}`}>
                            {rep.display_name ?? rep.name}
                          </a>
                        ) : null,
                      )}
                    </div>
                  ) : (
                    <span className="muted small">none</span>
                  )}
                </td>
                <td className="actions">
                  <Link to={`/runs/${r.id}`} className="button">
                    Open
                  </Link>
                  {isAdmin && (
                    <button className="link danger" onClick={() => setDeletingRun(r)}>
                      Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}

      {running && hub && (
        <RunPipelineDialog
          hub={hub}
          project={{ id: project.id, name: project.name }}
          onClose={() => setRunning(false)}
          onSubmitted={() => {
            setRunning(false);
            load();
          }}
        />
      )}
      {deletingProject && (
        <ConfirmDialog
          title="Delete project"
          confirmLabel="Delete project"
          onClose={() => setDeletingProject(false)}
          onConfirm={async () => {
            await api.del(`/api/projects/${project.id}`);
            navigate('/', { replace: true });
          }}
        >
          <p>
            Delete <b>{project.name}</b> with {project.runs.length} run{project.runs.length === 1 ? '' : 's'}? The converted
            assets are removed from storage and any pipeline jobs are cancelled and purged from the hub. This cannot be undone.
          </p>
        </ConfirmDialog>
      )}
      {deletingRun && (
        <ConfirmDialog
          title="Delete run"
          confirmLabel="Delete run"
          onClose={() => setDeletingRun(null)}
          onConfirm={async () => {
            await api.del(`/api/runs/${deletingRun.id}`);
            load();
          }}
        >
          <p>
            Delete run <span className="mono">{deletingRun.id}</span> ({deletingRun.asset_count} assets,{' '}
            {formatBytes(deletingRun.total_bytes)})? Its volumes, meshes and thumbnail are removed from storage. The project
            and its other runs stay.
          </p>
        </ConfirmDialog>
      )}
    </div>
  );
}
